import { createContext, useContext, ReactNode, useCallback } from 'react';
import { useParams } from '@/util/react-router-dom';
import { EncounterResponse } from '@/api/api.schemas';
import { getGetEncountersQueryKey, useGetEncounters } from '@/api/get-encounter';
import { useQueryClient } from '@tanstack/react-query';
import { Loader } from '@/components/loaders/loader';
import { BrandRedirect } from '@/components/brand-redirect';
import { isBrand, useBranding } from '@/outlets/branding-outlet';

interface EncounterContextType {
  encounter: EncounterResponse;
  invalidateEncounter: () => Promise<void>;
}

const EncounterContext = createContext<EncounterContextType | undefined>(undefined);

interface EncounterProviderProps {
  children: ReactNode;
}

export function EncounterProvider({ children }: EncounterProviderProps) {
  const { visitId } = useParams();
  const { brandName } = useBranding();
  const { data, isLoading } = useGetEncounters(Number(visitId));

  const queryClient = useQueryClient();

  const invalidateEncounter = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: getGetEncountersQueryKey(Number(visitId)) });
  }, [queryClient, visitId]);

  if (isLoading) {
    return <Loader type="large" />;
  }

  if (!data) {
    return null;
  }

  const { network } = data.encounter;
  if (network && isBrand(network) && brandName && network !== brandName) {
    return <BrandRedirect brand={network} />;
  }

  return (
    <EncounterContext.Provider value={{ encounter: data.encounter, invalidateEncounter }}>
      {children}
    </EncounterContext.Provider>
  );
}

export function useEncounter() {
  const context = useContext(EncounterContext);
  if (context === undefined) {
    throw new Error('useEncounter must be used within an EncounterProvider');
  }
  return context;
}
